import {
  EvoBuilderSubEvent,
  postEvent,
} from "@/api/privateRequests/events/postEvent";
import { getPlayer } from "@/api/privateRequests/players/getPlayer";
import { FloatingRailSlot } from "@/components/molecules/FloatingRailSlot/FloatingRailSlot";
import { PlayersToolbar } from "@/components/organisms/PlayersToolbar/PlayersToolbar";
import { canonicalUrl } from "@/config/canonical";
import { PINNED_ASSET_SLUG } from "@/config/game";
import { headingVersionName, useEvoBuilderCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { Button as DesignSystemButton } from "@/design-system/Button/Button";
import { trackFilter, trackShare } from "@/events/evolutionsMixpanel";
import { storeSignupContext } from "@/events/signupTracking";
import { LOGIN_GATE_TRIGGERS } from "@/events/signupTriggers";
import { WingsCupBanner } from "@/features/WingsCup/WingsCup";
import { useGoBack } from "@/hooks/useGoBack";
import { useInFlowHeight } from "@/hooks/useInFlowHeight";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useSticky } from "@/hooks/useSticky";
import {
  PlayerNotFound,
  isNotFoundError,
} from "@/pages/DetailedPlayer/PlayerNotFound";
import {
  addChainRecommendationToChain,
  addChainToEvolutionChain,
  addPlayerToEvolutionBuilder,
  addStepToEvolutionChain,
  applyEvolutionFilters,
  chainsAwaitingRevealSelector,
  clearAllEvolutionBuilderFilters,
  clearEvolutionBuilderFilter,
  clearEvolutionBuilderFilterOption,
  clearEvolutionChain,
  closePathwayDraft,
  evolutionBuilderActiveFiltersSelector,
  evolutionBuilderPlayStyleFiltersSelector,
  evolutionBuilderPlayerFiltersSelector,
  evolutionFiltersSelector,
  evolutionsBuilderSelector,
  fetchEvolutionChainRecommendations,
  getCurrentEvolutionPlayerSelector,
  getNoMoreStepsAvailableSelector,
  getTotalUpgradesSelector,
  openPathwayDraft,
  removeStepFromChain,
  revealChains,
  setEvolutionBuilderActiveFilter,
  setExcludedPlayStyles,
  setExcludedPlayStylesPlus,
  trackBuilderFiltersApplied,
  updatePlayerRoleChosenPlayer,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import { evolutionsSelector } from "@/redux/evolutions/evolutionsSlice";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import {
  PlayerRole,
  generateNavigationUrl,
  playersSelector,
  versionNameBySlug,
  versionUrlSlug,
} from "@/redux/players/playersSlice";
import { addPlayerOrChangeRole } from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { AppDispatch } from "@/redux/store";
import {
  getUserSelector,
  usedFeature,
  userIsLoggedInSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import {
  type EvolutionPickerSelection,
  evolutionPathFromParam,
  evolutionPathPair,
  evolutionPathParam,
  isPathwayEvolution,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { saveEvolution } from "@repo/shared/redux";
import { usesEvoPath } from "@repo/shared/redux";
import { useCreditGate } from "@repo/shared/redux";
import type {
  EvolutionChainRecommendation,
  EvolutionStep,
} from "@repo/shared/types";
import { capitalizeWordsAndRemoveDashes } from "@repo/shared/utils";
import { Save, Share } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { ToolbarAction } from "../EvolutionsPage/EvolutionsPage";
import { EvolutionsPickerBottomSheet } from "../EvolutionsPickerBottomSheet";
import type { ChainWallMode } from "../sections/EvolutionChain";
import { EvolutionChain } from "../sections/EvolutionChain";
import { EvolutionsSteps } from "../sections/EvolutionsSteps";
import { EvolutionsStickySidebar } from "../sections/EvolutionsStickySidebar";
import {
  CardBuilderTopSection,
  CardBuilderTopSectionSkeleton,
} from "./CardBuilderTopSection";
import { EvolutionsBuilderChainRecommendations } from "./components/EvolutionsBuilderChainRecommendations";
import { PathwaySheet } from "./components/PathwaySheet";

export const EvolutionsBuilder = () => {
  const { t } = useTranslation("evoBuilder");
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const topToPage = useSelector(topToPageSelector);
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
    offset: isTouchDevice ? 0 + topToPage.value : -76 + topToPage.value,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];

  // The pinned player bar's height, reported once it pins; until then a
  // typical height stands in.
  const [pinnedBarHeight, setPinnedBarHeight] = useState(
    isTouchDevice ? 140 : 98,
  );
  // Pin the chain as its top meets the bar's bottom. The sentinel sits one
  // section gap (16px) above the chain card.
  const [scrolledIntoChain, refChain] = useSticky({
    triggerOn: "bottom",
    offset: topToPage.value + pinnedBarHeight - 16,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const [showEvolutionsPickerBottomSheet, setShowEvolutionsPickerBottomSheet] =
    useState(false);
  // Player roles for the chosen player (different from filter role)
  const [playerRolesChosenPlayer, setPlayerRolesChosenPlayer] = useState<
    PlayerRole[]
  >([]);
  // Memoize the player role IDs to prevent infinite loops in useEffect dependencies
  const playerRoleIdsChosenPlayer = useMemo(
    () => playerRolesChosenPlayer.map((r) => r.id),
    [playerRolesChosenPlayer],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [elementOpened, setElementOpened] = useState<ToolbarAction>("none");
  const { resourceId, name, version } = useParams();
  // Keyed on the id so browsing on to another player clears it.
  const [notFoundResourceId, setNotFoundResourceId] = useState<string>();

  // Refs for outside click handling
  const refPlayerRole = useOutsideClick(() => {
    if (elementOpened === "playerRole") setElementOpened("none");
  });
  const refIncludeExclude = useOutsideClick(() => {
    if (elementOpened === "includeExclude") setElementOpened("none");
  });
  const refFilters = useOutsideClick(() => {
    if (elementOpened === "filters") setElementOpened("none");
  });

  // Selectors
  const { user } = useSelector(getUserSelector);
  const isLoggedIn = useSelector(userIsLoggedInSelector);

  const dispatch = useDispatch<AppDispatch>();
  const chainsAwaitingReveal = useSelector(chainsAwaitingRevealSelector);
  const {
    basePlayer,
    currentEvolutionChain,
    currentPathwayChain,
    evolutionSteps,
    stepStatus,
    chainRecommendationsStatus,
    evolutionChainRecommendations,
    activeFilter,
    currentStepData,
    requestCounters,
  } = useSelector(evolutionsBuilderSelector);
  const totalUpgrades = useSelector(getTotalUpgradesSelector);
  const currentEvolutionPlayer = useSelector(getCurrentEvolutionPlayerSelector);
  // The chain joins the pinned player bar once scrolled under it. It only
  // ever pins after the player bar has (its trigger sits lower), and an
  // empty chain has nothing to keep in view.
  const pinChain =
    scrollPastTopBar && scrolledIntoChain && currentEvolutionChain.length > 0;
  const [chainFlowRef, chainFlowHeight] = useInFlowHeight(!pinChain);
  const creditGate = useCreditGate("evo-chains-club");
  const { includedEvolutions, mustIncludeEvolutions } = useSelector(
    evolutionFiltersSelector,
  );
  const { evolutions, expiredEvolutions } = useSelector(evolutionsSelector);
  const hasExpiredIncluded = useMemo(() => {
    if (!expiredEvolutions.length) return false;
    const activeSet = new Set([
      ...includedEvolutions,
      ...mustIncludeEvolutions,
    ]);
    return expiredEvolutions.some((evo) => activeSet.has(evo.id));
  }, [expiredEvolutions, includedEvolutions, mustIncludeEvolutions]);
  const { noMoreStepsAvailable, noEvolutionsFoundForThisPlayer } = useSelector(
    getNoMoreStepsAvailableSelector,
  );
  const [awaitingLogin, setAwaitingLogin] = useState<
    "save" | "unlock-chains" | undefined
  >();
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const goBack = useGoBack();
  const versionAssets = useSelector(getVersionAssetsSelector);

  // Check if we came from squad builder via query param
  const [playerQueryParamsRef] = useSearchParams();
  const comingFromSquadBuilder = useMemo(() => {
    return playerQueryParamsRef.get("from") === "squad-builder";
  }, []);
  const [playerQueryParams, setPlayerQueryParams] = useSearchParams();
  /*
    Two reasons the chain steps stay covered, and they need different buttons:
    nobody is signed in, or this is one of the user's own evolution players and
    the ids are withheld until a credit is spent. Both are answered by the same
    click target.

    The reveal wall is gated on the same predicate as the charge — only a player
    already on an evo path is metered (see qualifying.ts). Withheld ids on any
    other player are not the user's to buy, so covering the steps there would
    price something that is free.
  */
  const chainWall: ChainWallMode | undefined = !isLoggedIn
    ? "login"
    : chainsAwaitingReveal &&
        usesEvoPath((currentEvolutionPlayer || basePlayer)?.evoPathId)
      ? "reveal"
      : undefined;
  const evolutionBuilderFilters = useSelector(
    evolutionBuilderPlayerFiltersSelector,
  );
  const evolutionBuilderActiveFilters = useSelector(
    evolutionBuilderActiveFiltersSelector,
  );
  const playStyleFilters = useSelector(
    evolutionBuilderPlayStyleFiltersSelector,
  );
  const hasFilters = useMemo(() => {
    return (
      evolutionBuilderActiveFilters.length > 0 ||
      playStyleFilters.excludedPlayStyles.length > 0 ||
      playStyleFilters.excludedPlayStylesPlus.length > 0
    );
  }, [evolutionBuilderActiveFilters, playStyleFilters]);

  const playStyleOptions = useMemo(() => {
    const psFilter = evolutionBuilderFilters.find(
      (f) => f.key === "playStyles",
    );
    const psPlusFilter = evolutionBuilderFilters.find(
      (f) => f.key === "playStylesPlus",
    );
    return {
      playStyles: psFilter?.type === "SELECTOR_FILTER" ? psFilter.options : [],
      playStylesPlus:
        psPlusFilter?.type === "SELECTOR_FILTER" ? psPlusFilter.options : [],
    };
  }, [evolutionBuilderFilters]);

  const excludedPlayStyleChips = useMemo(() => {
    const chips = [];
    if (playStyleFilters.excludedPlayStyles.length > 0) {
      chips.push({
        ids: playStyleFilters.excludedPlayStyles,
        options: playStyleOptions.playStyles,
        filterKey: "playStyles" as const,
      });
    }
    if (playStyleFilters.excludedPlayStylesPlus.length > 0) {
      chips.push({
        ids: playStyleFilters.excludedPlayStylesPlus,
        options: playStyleOptions.playStylesPlus,
        filterKey: "playStylesPlus" as const,
      });
    }
    return chips;
  }, [playStyleFilters, playStyleOptions]);
  const canSave = useMemo(
    () => !(!basePlayer?.resourceId || currentEvolutionChain.length === 0),
    [basePlayer?.resourceId, currentEvolutionChain.length],
  );
  const { playerRoles } = useSelector(playersSelector);

  const trackEvoBuild = (
    subEvent: EvoBuilderSubEvent,
    evoPath: number[] = currentEvolutionChain,
    pathwayPath: number[][] = currentPathwayChain,
  ) => {
    if (!basePlayer?.resourceId || basePlayer.evoPathId !== undefined) return;
    // playerPathId keeps the dash id the trending tables are keyed on; the
    // pathway travels next to it.
    postEvent({
      event: "evo-builder-player",
      resourceId: basePlayer.resourceId,
      playerPathId: `${basePlayer.evoPathId ?? basePlayer.resourceId}-${evoPath.join("-")}`,
      ...evolutionPathPair(evoPath, pathwayPath),
      subEvent,
    });
  };

  const chainParam = (chain: number[], pathway: number[][]) =>
    evolutionPathParam(chain, pathway);

  const navigateToPlayerDetails = () => {
    const playerToNavigate = currentEvolutionPlayer;
    if (!playerToNavigate) return;

    playerQueryParams.set(
      "player-role-id",
      (
        playerRolesChosenPlayer[0]?.id || playerToNavigate.playerRoleId
      )?.toString(),
    );

    if (currentEvolutionChain.length > 0) {
      playerQueryParams.set(
        "evolution-path",
        chainParam(currentEvolutionChain, currentPathwayChain),
      );
    }

    if (playerToNavigate.evoPathId) {
      playerQueryParams.set(
        "evo-path-id",
        playerToNavigate.evoPathId.toString(),
      );
    }

    const navLink = generateNavigationUrl({
      name: playerToNavigate.name,
      resourceId: playerToNavigate.resourceId,
      playerRoleId: playerToNavigate.playerRoleId,
      evoPathId: playerToNavigate.evoPathId,
      evolutionPath: currentEvolutionChain,
      pathwayPath: currentPathwayChain,
      basePath: "/evolutions/builder/details",
    });

    trackEvoBuild("detailedPlayerView");
    navigate(navLink, {
      state: {
        p: playerToNavigate,
      },
    });
  };

  const handleDownloadAssets = () => {
    const versionSlug =
      version ||
      versionUrlSlug(versionAssets[currentEvolutionPlayer?.versionId]);
    let queryParams = new URLSearchParams();
    if (currentEvolutionChain?.length > 0) {
      queryParams.set(
        "evolution-path",
        chainParam(currentEvolutionChain, currentPathwayChain),
      );
    }
    if (currentEvolutionPlayer?.evoPathId) {
      queryParams.set(
        "evo-path-id",
        currentEvolutionPlayer.evoPathId?.toString(),
      );
    }
    queryParams.set("is-evo-builder", "true");
    const queryString = queryParams.toString();
    trackEvoBuild("download");
    trackShare({ subsection: "Builder" });
    navigate(
      `/players/${currentEvolutionPlayer?.name}/${versionSlug}/${currentEvolutionPlayer?.resourceId}/images${queryString ? `?${queryString}` : ""}`,
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(
        saveEvolution({
          resourceId: basePlayer.resourceId,
          evolutionPath: currentEvolutionChain,
          pathwayPath: currentPathwayChain,
          evoPathId: basePlayer.evoPathId,
        }),
      ).unwrap();
      trackEvoBuild("save");
      dispatch(
        addAlert({
          type: "success",
          description: t("alerts.saved"),
          persistent: true,
          link: {
            label: t("alerts.viewSaved"),
            to: "/evolutions/my-evolutions/saved",
          },
        }),
      );
      // save (evo_player) is fired server-side in peareasy-services
      // (SavedEvolutionPlayersController) when this POST hits the backend.
    } catch {
      dispatch(
        addAlert({
          type: "error",
          description: t("alerts.saveFailed"),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  // Save is one click: nothing applied is a warning, logged out parks the
  // save until sign-in completes.
  const handleSaveClicked = () => {
    if (!canSave) {
      dispatch(
        addAlert({ type: "warning", description: t("alerts.nothingToSave") }),
      );
      return;
    }
    if (!isLoggedIn) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "save-evo",
        }),
      );
      // Same trigger the modal itself wrote; this adds the section/subsection
      // the hook-derived write cannot know.
      storeSignupContext(LOGIN_GATE_TRIGGERS.SAVE_EVO, {
        section: "Evolutions",
        subsection: "Builder",
      });
      setAwaitingLogin("save");
      return;
    }
    handleSave();
  };

  const handleToolbarAction = (action: ToolbarAction) => {
    if (action === "saveEvolution") {
      handleSaveClicked();
      return;
    }
    if (action === "downloadAssets") {
      handleDownloadAssets();
      return;
    }
    if (!isTouchDevice && action === "includeExclude") {
      // Open the include exclude bottom sheet.
      setShowEvolutionsPickerBottomSheet((prev) => !prev);
      return;
    }
    setElementOpened(elementOpened === action ? "none" : action);
  };

  const handlePlayerClicked = () => {
    navigateToPlayerDetails();
  };

  /*
    The reveal the user already asked for by pressing the button that names the
    cost. Run at the click when signed in, or once the login it sent them to
    completes.
  */
  const revealChainsIfAllowed = () => {
    const qualifying = usesEvoPath(
      (currentEvolutionPlayer || basePlayer)?.evoPathId,
    );
    if (!creditGate({ qualifying })) return;
    dispatch(revealChains());
  };

  /*
    Logging in refetches the chains, and that response is the one a reveal has
    to be spent against: dispatched against the logged-out chainSetId it races
    the refetch, which lands second and paints the withheld ids back over the
    ones just paid for. So park the request in flight at login and wait for a
    later one to settle.
  */
  const parkedReveal = useRef<
    { requestId: string; resourceId?: number } | undefined
  >(undefined);

  useEffect(() => {
    if (!awaitingLogin || !isLoggedIn) return;
    if (awaitingLogin === "save") handleSave();
    if (awaitingLogin === "unlock-chains")
      parkedReveal.current = {
        requestId: requestCounters.chainRecommendations,
        resourceId: (currentEvolutionPlayer || basePlayer)?.resourceId,
      };
    setAwaitingLogin(undefined);
  }, [isLoggedIn, awaitingLogin]);

  useEffect(() => {
    const parked = parkedReveal.current;
    if (!parked || chainRecommendationsStatus !== "fulfilled") return;
    if (requestCounters.chainRecommendations === parked.requestId) return;
    parkedReveal.current = undefined;
    // Not the player the click was about any more — a spend here would be on
    // whoever the user browsed to next.
    if (
      parked.resourceId !== (currentEvolutionPlayer || basePlayer)?.resourceId
    )
      return;
    // Signed in, the fetch hands the ids over outright unless this player is
    // metered, so only a still-withheld set is worth spending on.
    if (chainsAwaitingReveal) revealChainsIfAllowed();
  }, [
    chainRecommendationsStatus,
    requestCounters.chainRecommendations,
    chainsAwaitingReveal,
  ]);

  const evoPathIdParam = playerQueryParams.get("evo-path-id");

  // Load player from URL params if accessing directly via URL
  useEffect(() => {
    const fetchPlayer = async () => {
      const playerRoleId = playerQueryParams.get("player-role-id");
      const player = await getPlayer({
        resourceId: Number(resourceId),
        playerRoleId: playerRoleId ? +playerRoleId : undefined,
        evoPathId: playerQueryParams.get("evo-path-id"),
        backendPlayerType: "evo-builder",
      }).catch((error) => {
        if (isNotFoundError(error)) setNotFoundResourceId(resourceId);
        throw error;
      });

      const urlEvolutionPath = evolutionPathFromParam(
        playerQueryParams.get("evolution-path"),
      );
      const chain = urlEvolutionPath?.evolutionPath ?? [];
      dispatch(
        addPlayerToEvolutionBuilder({
          player: {
            ...player,
            resourceId: Number(resourceId),
            playerRoleId: playerRoleId ? +playerRoleId : undefined,
            evoPathId: playerQueryParams.get("evo-path-id"),
            evolutionPath: chain,
            pathwayPath: urlEvolutionPath?.pathwayPath,
          },
        }),
      );

      dispatch(
        addChainToEvolutionChain({
          evolutionChain: chain,
          pathwayChain: urlEvolutionPath?.pathwayPath,
        }),
      );
    };

    // Keyed on which player the URL points at, not merely "is one loaded" —
    // otherwise back/forward between two builder URLs keeps the stale card.
    const storeMatchesUrl =
      basePlayer &&
      String(basePlayer.resourceId) === resourceId &&
      String(basePlayer.evoPathId ?? "") === (evoPathIdParam ?? "");

    if (resourceId && !storeMatchesUrl && !comingFromSquadBuilder) {
      fetchPlayer();
    }
  }, [resourceId, basePlayer, comingFromSquadBuilder, evoPathIdParam]);

  /*
    The name/version segments are free text as far as the router is concerned,
    so /evolutions/builder/<anything>/<id> renders that text as the h1, title
    and self-referencing canonical. Normalise to the player's real slug once
    it's known, otherwise every variant is an indexable duplicate.
  */
  useEffect(() => {
    if (!basePlayer?.name || !name) return;
    const versionSlug = versionUrlSlug(versionAssets[basePlayer.versionId]);
    if (!versionSlug) return;

    const canonicalPath = generateNavigationUrl({
      name: basePlayer.name,
      resourceId: basePlayer.resourceId,
      versionSlug,
      basePath: "/evolutions/builder",
    });

    if (canonicalPath !== pathname) {
      navigate(`${canonicalPath}${window.location.search}`, { replace: true });
    }
  }, [basePlayer, name, pathname, versionAssets]);

  // Set player role from squad builder query params
  useEffect(() => {
    if (comingFromSquadBuilder && playerRoles.length > 0) {
      const playerRole = playerRoles.find(
        (role) => role.id === +playerQueryParams.get("player-role-id"),
      );
      if (playerRole) {
        setPlayerRolesChosenPlayer([playerRole]);
      }
    }
  }, [comingFromSquadBuilder, playerRoles]);

  const handleRemoveStep = (indexInChain: number) => {
    const localQueryParams = new URLSearchParams(playerQueryParams);
    if (indexInChain === 0) {
      localQueryParams.delete("evolution-path");
    } else {
      localQueryParams.set(
        "evolution-path",
        chainParam(
          currentEvolutionChain.slice(0, indexInChain),
          currentPathwayChain.slice(0, indexInChain),
        ),
      );
    }
    setPlayerQueryParams(localQueryParams, {
      replace: true,
      preventScrollReset: true,
    });
    dispatch(removeStepFromChain(indexInChain));
  };

  const handleAddStep = (evolutionStep: EvolutionStep) => {
    const localQueryParams = new URLSearchParams(playerQueryParams);
    localQueryParams.set(
      "evolution-path",
      chainParam(
        [...currentEvolutionChain, evolutionStep.evolutionId],
        [...currentPathwayChain, evolutionStep.pathwayPath ?? []],
      ),
    );
    setPlayerQueryParams(localQueryParams, {
      replace: true,
      preventScrollReset: true,
    });
    dispatch(usedFeature({ feature: "CLICKED_EVO_BUILDER_STEP" }));
    dispatch(addStepToEvolutionChain(evolutionStep));
  };

  // A pathway evolution opens the pathway sheet; the chain changes on its
  // "Add to Chain".
  const handleStepClicked = (evolutionStep: EvolutionStep) => {
    const evolution = [...evolutions, ...expiredEvolutions].find(
      (evo) => evo.id === evolutionStep.evolutionId,
    );
    if (evolution && isPathwayEvolution(evolution)) {
      dispatch(openPathwayDraft(evolutionStep));
      return;
    }
    handleAddStep(evolutionStep);
  };

  useEffect(
    () => () => {
      dispatch(closePathwayDraft());
    },
    [],
  );

  const handleClearChain = () => {
    const localQueryParams = new URLSearchParams(playerQueryParams);
    localQueryParams.delete("evolution-path");
    setPlayerQueryParams(localQueryParams, {
      replace: true,
      preventScrollReset: true,
    });
    dispatch(clearEvolutionChain());
  };

  const handleSelectPlayerRolesChosenPlayer = (playerRoles: PlayerRole[]) => {
    // Update redux with the first selected role for backward compatibility
    dispatch(updatePlayerRoleChosenPlayer(playerRoles[0]?.id));
    setPlayerRolesChosenPlayer(playerRoles);
    onFiltersUpdated(playerRoles);
  };

  const handleAddChainToEvolutionChain = (
    chain: EvolutionChainRecommendation,
  ) => {
    dispatch(usedFeature({ feature: "CLICKED_EVO_BUILDER_STEP" }));
    dispatch(addChainRecommendationToChain(chain));
    const added = evolutionPathPair(chain.evolutionIds, chain.pathwayPath);
    const evolutionPath = [...currentEvolutionChain, ...added.evolutionPath];
    const pathwayPath = [...currentPathwayChain, ...added.pathwayPath];
    trackEvoBuild("chainRecommendation", evolutionPath, pathwayPath);
    const localQueryParams = new URLSearchParams(playerQueryParams);
    localQueryParams.set(
      "evolution-path",
      chainParam(evolutionPath, pathwayPath),
    );
    setPlayerQueryParams(localQueryParams, {
      replace: true,
      preventScrollReset: true,
    });
  };

  // Fetch chain recommendations
  useEffect(() => {
    if (currentEvolutionPlayer?.assetId && !hasExpiredIncluded) {
      // Ungated on purpose: this call returns the withheld chains that REVEAL
      // CHAIN is drawn on. The spend lives on the unlock click below.
      dispatch(
        fetchEvolutionChainRecommendations({
          playerRoleIds: playerRoleIdsChosenPlayer,
        }),
      );
    }
  }, [
    currentEvolutionChain.length,
    playerRoleIdsChosenPlayer,
    currentEvolutionPlayer?.assetId,
    // maxChainLength,
    includedEvolutions,
    mustIncludeEvolutions,
    evolutionBuilderFilters,
    // Chains fetched logged out come back withheld for everyone, and nothing
    // else re-asks for them once there is a session to fetch them under.
    isLoggedIn,
  ]);

  const handleExcludeExpiredEvolutions = () => {
    const expiredIds = new Set(expiredEvolutions.map((evo) => evo.id));
    dispatch(
      applyEvolutionFilters({
        includedEvolutions: includedEvolutions.filter(
          (id) => !expiredIds.has(id),
        ),
        mustIncludeEvolutions: mustIncludeEvolutions.filter(
          (id) => !expiredIds.has(id),
        ),
      }),
    );
  };

  const sendToSquadBuilder = () => {
    dispatch(
      addPlayerOrChangeRole({
        player: {
          ...currentEvolutionPlayer,
          evolutionPath: currentEvolutionChain,
          pathwayPath: currentPathwayChain,
        },
      }),
    );
    // A destination, not a back step: this action exists to hand the player to
    // the squad builder, whether or not the visitor arrived from there.
    navigate("/squad-builder");
  };

  const handleClickUnlockChains = () => {
    if (!isLoggedIn) {
      // The click is the request; logging in is only the detour. Resume the
      // reveal once it completes.
      setAwaitingLogin("unlock-chains");
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "unlock-chains",
        }),
      );
      storeSignupContext("unlock_chains", {
        section: "Evolutions",
        subsection: "Builder",
      });
      return;
    }

    /*
      A deliberate spend — the user pressed the button that names the cost — so
      unlike the background chain fetch, a refusal here gets the dialog rather
      than silence.
    */
    revealChainsIfAllowed();
  };

  const handleSelectActiveFilter = (filter: EvolutionPickerSelection) => {
    dispatch(setEvolutionBuilderActiveFilter(filter));
  };

  // Tracking is fired from a thunk so the active filters are read from the
  // store at dispatch time (fresh, post-apply) rather than from a stale render
  // snapshot. `chosenPlayerRoles` is passed explicitly since it lives in
  // component state; it's provided on the role-selection path where this is
  // called right after a `setState`.
  const onFiltersUpdated = (
    chosenPlayerRoles: PlayerRole[] = playerRolesChosenPlayer,
  ) => {
    dispatch(
      trackBuilderFiltersApplied({
        hasChosenPlayerRole: chosenPlayerRoles.length > 0,
      }),
    );
  };
  // Real names once the player has loaded; the URL slugs fill in before
  // getPlayer resolves so the tags are right on first paint.
  const playerLabel = useMemo(() => {
    if (!name) return "";
    return [
      basePlayer?.name || capitalizeWordsAndRemoveDashes(name),
      headingVersionName(
        versionNameBySlug(versionAssets, version) ??
          capitalizeWordsAndRemoveDashes(version),
      ),
    ]
      .filter(Boolean)
      .join(" ");
  }, [name, version, basePlayer?.name, versionAssets]);

  const {
    title: helmetTitle,
    pageTitle,
    description: helmetDescription,
    subtitle,
  } = useEvoBuilderCopy(playerLabel);

  // Self-referencing so each player page can be indexed on its own; pathname
  // drops the filter query params, which are not distinct pages.
  const helmetCanonicalLink = canonicalUrl(pathname);
  if (resourceId && notFoundResourceId === resourceId) {
    return <PlayerNotFound />;
  }
  return (
    <main className="mx-auto min-h-screen bg-background">
      <HeroSection
        helmetTitle={helmetTitle}
        pageTitle={pageTitle}
        helmetDescription={helmetDescription}
        subtitle={subtitle}
        helmetCanonicalLink={helmetCanonicalLink}
        back={goBack}
        alignCenter={false}
        shouldBeInTheDocumentFlow
      >
        <div ref={refTopBar} />
        {!basePlayer && <CardBuilderTopSectionSkeleton />}
        {basePlayer && (
          <CardBuilderTopSection
            scrollPastTopBar={scrollPastTopBar}
            basePlayer={basePlayer}
            currentEvolutionPlayer={currentEvolutionPlayer}
            isEvolutionPlayer={currentEvolutionChain.length > 0}
            handlePlayerClicked={handlePlayerClicked}
            loading={stepStatus === "pending"}
            currentStepData={currentStepData}
            upgrades={totalUpgrades}
            onPinnedHeightChange={setPinnedBarHeight}
            stickyChain={
              pinChain && (
                <EvolutionChain
                  evolutionChain={currentEvolutionChain}
                  onClickStep={handleRemoveStep}
                  onClickClearChain={handleClearChain}
                  isSticky
                />
              )
            }
          />
        )}
      </HeroSection>
      <PlayersToolbar
        hasFilters={hasFilters}
        searchTerm={searchTerm}
        saving={saving}
        setSearchTerm={setSearchTerm}
        handleToolbarAction={handleToolbarAction}
        isMobile={isMobile}
        sortBy="meta-rating"
        setSortBy={() => {}}
        selectedPlayerRoles={playerRolesChosenPlayer}
        pageType="evolutions-builder"
        isEvolutionsBuilderContext={true}
        // The applied chain lives in its own state slice field, not on the
        // player object — attach it so the compare toggle stages the build's
        // identity (and the comparison fetch grades the evolved version).
        // evolutionData must go: a base picked off an evo-DB row still
        // carries that row's metadata, whose evolutionPath would shadow the
        // applied chain everywhere the identity resolves evolutionData first.

        currentEvolutionPlayer={
          currentEvolutionPlayer
            ? {
                ...currentEvolutionPlayer,
                evolutionData: undefined,
                evolutionPath: currentEvolutionChain,
                pathwayPath: currentPathwayChain,
              }
            : undefined
        }
        includedEvolutionsCount={
          includedEvolutions.length === 0
            ? evolutions.length
            : includedEvolutions.length
        }
        activeFilters={evolutionBuilderActiveFilters}
        onClearFilter={(filterKey) =>
          dispatch(clearEvolutionBuilderFilter(filterKey))
        }
        onClearFilterOption={(filterKey, option) =>
          dispatch(clearEvolutionBuilderFilterOption({ filterKey, option }))
        }
        onClearAllFilters={() => dispatch(clearAllEvolutionBuilderFilters())}
        excludedPlayStyleChips={excludedPlayStyleChips}
        onClearExcludedPlayStyle={(filterKey, id) => {
          if (filterKey === "playStyles") {
            dispatch(
              setExcludedPlayStyles(
                playStyleFilters.excludedPlayStyles.filter((x) => x !== id),
              ),
            );
          } else {
            dispatch(
              setExcludedPlayStylesPlus(
                playStyleFilters.excludedPlayStylesPlus.filter((x) => x !== id),
              ),
            );
          }
        }}
      />

      <div className="max-w-content mx-auto">
        <section className="flex flex-col gap-y-4 px-2 large:px-16 touch-device:px-4">
          <div ref={refChain} />
          {/* Once scrolled past, the chain moves into the pinned player bar
              (see stickyChain above); a spacer of its in-flow height keeps
              the steps from jumping. */}
          {pinChain ? (
            <div style={{ height: chainFlowHeight ?? 0 }} />
          ) : (
            <div ref={chainFlowRef}>
              <EvolutionChain
                evolutionChain={currentEvolutionChain}
                onClickStep={handleRemoveStep}
                onClickClearChain={handleClearChain}
              />
            </div>
          )}
          {basePlayer && <WingsCupBanner unit="evo-builder" />}

          {/* The end of the chain: the same closing card as the PlayStyles
              Lab, with the build's actions right there. */}
          {noMoreStepsAvailable && (
            <div className="flex w-full flex-col items-center gap-4 rounded-[20px] border border-edge-strong bg-surface px-4 py-6">
              <p className="text-center text-sm text-foreground-muted">
                {noEvolutionsFoundForThisPlayer
                  ? t("noEvolutionsForPlayer")
                  : t("noMoreSteps")}
              </p>
              {currentEvolutionChain.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <DesignSystemButton
                    variant="primary"
                    size="small"
                    icon={<Save className="size-4" />}
                    onClick={handleSaveClicked}
                    disabled={saving}
                  >
                    {t("common:save")}
                  </DesignSystemButton>
                  {!isTouchDevice && (
                    <DesignSystemButton
                      variant="secondary"
                      size="small"
                      icon={<Share className="size-4" />}
                      onClick={handleDownloadAssets}
                    >
                      {t("common:share")}
                    </DesignSystemButton>
                  )}
                  <DesignSystemButton
                    variant="secondary"
                    size="small"
                    onClick={handleClearChain}
                  >
                    {t("common:reset")}
                  </DesignSystemButton>
                </div>
              )}
            </div>
          )}

          {hasExpiredIncluded ? (
            <div className="flex w-full flex-col items-start gap-y-3 rounded-[20px] border border-edge-strong bg-surface p-4">
              <p className="text-sm text-foreground-body">
                {t("expiredIncluded")}
              </p>
              <DesignSystemButton
                variant="secondary"
                size="small"
                onClick={handleExcludeExpiredEvolutions}
              >
                {t("excludeExpired")}
              </DesignSystemButton>
            </div>
          ) : (
            (evolutionChainRecommendations.length > 0 ||
              chainRecommendationsStatus === "pending") && (
              <EvolutionsBuilderChainRecommendations
                onAddChainToEvolutionChain={handleAddChainToEvolutionChain}
                loading={chainRecommendationsStatus === "pending"}
                evolutionChainRecommendations={evolutionChainRecommendations}
                onClickUnlockChains={handleClickUnlockChains}
                triesLeft={user?.evoBuilderTriesLeft || 0}
                chainWall={chainWall}
                searchTerm={searchTerm}
              />
            )
          )}
          <EvolutionsSteps
            onClickStep={handleStepClicked}
            evolutionSteps={evolutionSteps}
            currentEvolutionChain={currentEvolutionChain}
            loading={stepStatus === "pending"}
            playerRoleIdsChosenPlayer={playerRoleIdsChosenPlayer}
            searchTerm={searchTerm}
          />
        </section>
        {(elementOpened === "playerRole" ||
          elementOpened === "includeExclude" ||
          elementOpened === "filters") &&
          basePlayer && (
            <EvolutionsStickySidebar
              elementOpened={elementOpened}
              handleToolbarAction={handleToolbarAction}
              isTouchDevice={isTouchDevice}
              playerRoles={playerRoles}
              playerRole={playerRolesChosenPlayer[0]}
              handleSelectPlayerRole={() => {}}
              selectedPlayerRoles={playerRolesChosenPlayer}
              handleSelectPlayerRoles={handleSelectPlayerRolesChosenPlayer}
              refPlayerRole={refPlayerRole}
              refIncludeExclude={refIncludeExclude}
              refFilters={refFilters}
              playerFilters={evolutionBuilderFilters}
              hideRolesPlayerCannotPlay={basePlayer.possiblePositions}
              builderContext={
                !!basePlayer
                  ? "evolution-builder-with-player"
                  : "evolution-builder"
              }
              onFiltersUpdated={onFiltersUpdated}
            />
          )}
        <PathwaySheet
          playerRoleIds={playerRoleIdsChosenPlayer}
          onAddToChain={handleAddStep}
        />
        {showEvolutionsPickerBottomSheet && (
          <EvolutionsPickerBottomSheet
            isOpen={showEvolutionsPickerBottomSheet}
            onClose={() => {
              setShowEvolutionsPickerBottomSheet(false);
            }}
            onApply={(data) => {
              dispatch(applyEvolutionFilters(data));
              trackFilter({ subsection: "Builder", filters: ["evolutions"] });
            }}
            activeFilter={activeFilter}
            handleSelectActiveFilter={handleSelectActiveFilter}
            showExpired
            ignoreExcludedByDefault
          />
        )}
        {/* Floating counterpart to the compare pill (bottom-left): send the
            build back to the squad slot it was opened for. */}
        {comingFromSquadBuilder && (
          <FloatingRailSlot side="right" className="bottom-[var(--ad-gutter)]">
            <DesignSystemButton
              variant="primary"
              size="medium"
              onClick={sendToSquadBuilder}
            >
              {t("sendToSquad")}
            </DesignSystemButton>
          </FloatingRailSlot>
        )}
      </div>
    </main>
  );
};
